import { useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Home,
  UserPlus,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { useCommunity } from "@/contexts/CommunityContext";

const ROLES = [
  { value: "COMMUNITY_ADMIN", label: "Administrador del barrio" },
  { value: "RESIDENT", label: "Residente" },
  { value: "SECURITY", label: "Seguridad" },
  { value: "MAINTENANCE", label: "Mantenimiento" },
  { value: "AMENITY_STAFF", label: "Personal de amenities" },
] as const;

const ASSIGNMENT_TYPES = [
  { value: "OWNER", label: "Propietario" },
  { value: "LONG_TERM_TENANT", label: "Inquilino permanente" },
  { value: "SEASONAL_TENANT", label: "Inquilino temporal" },
  { value: "FAMILY_MEMBER", label: "Familiar" },
] as const;

type Role = (typeof ROLES)[number]["value"];
type AssignmentType = (typeof ASSIGNMENT_TYPES)[number]["value"];

export function MembersPage() {
  const utils = trpc.useUtils();
  const { selectedCommunityId } = useCommunity();
  const { data: communities } = trpc.amenity.getUserCommunities.useQuery();

  const currentCommunity = communities?.find(
    (c) => c.id === selectedCommunityId,
  );
  const isAdmin = currentCommunity?.role === "COMMUNITY_ADMIN";

  const { data: members, isLoading } = trpc.community.getMembers.useQuery(
    { communityId: selectedCommunityId || "" },
    { enabled: !!selectedCommunityId && isAdmin },
  );

  const { data: units } = trpc.community.getUnits.useQuery(
    { communityId: selectedCommunityId || "" },
    { enabled: !!selectedCommunityId && isAdmin },
  );

  const addMember = trpc.community.addMember.useMutation();
  const removeMember = trpc.community.removeMember.useMutation();
  const updateRole = trpc.community.updateMemberRole.useMutation();
  const assignUnit = trpc.community.assignUnit.useMutation();
  const removeAssignment = trpc.community.removeUnitAssignment.useMutation();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [newMember, setNewMember] = useState({
    email: "",
    role: "RESIDENT" as Role,
    unitId: "",
    newUnit: null as { unitNumber: string; blockSection: string } | null,
    assignmentType: "OWNER" as AssignmentType,
  });
  const [memberUnitForm, setMemberUnitForm] = useState<{
    unitId: string;
    newUnit: { unitNumber: string; blockSection: string } | null;
    assignmentType: AssignmentType;
  }>({ unitId: "", newUnit: null, assignmentType: "OWNER" });

  const invalidate = () => {
    if (!selectedCommunityId) return;
    utils.community.getMembers.invalidate({
      communityId: selectedCommunityId,
    });
    utils.community.getUnits.invalidate({
      communityId: selectedCommunityId,
    });
  };

  const handleAddMember = async () => {
    if (!selectedCommunityId || !newMember.email.trim() || addMember.isPending)
      return;
    const hasUnit = newMember.unitId || newMember.newUnit?.unitNumber;
    try {
      await addMember.mutateAsync({
        communityId: selectedCommunityId,
        email: newMember.email.trim(),
        role: newMember.role,
        unitId: newMember.unitId || undefined,
        newUnit: newMember.newUnit?.unitNumber
          ? {
              unitNumber: newMember.newUnit.unitNumber,
              blockSection: newMember.newUnit.blockSection || undefined,
            }
          : undefined,
        assignmentType: hasUnit ? newMember.assignmentType : undefined,
      });
      invalidate();
      setIsAddOpen(false);
      setNewMember({
        email: "",
        role: "RESIDENT",
        unitId: "",
        newUnit: null,
        assignmentType: "OWNER",
      });
      toast.success("Miembro agregado");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo agregar el miembro",
      );
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!selectedCommunityId) return;
    try {
      await removeMember.mutateAsync({
        communityId: selectedCommunityId,
        memberId,
      });
      invalidate();
      toast.success("Miembro eliminado");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo eliminar el miembro",
      );
    }
  };

  const handleUpdateRole = async (memberId: string, role: Role) => {
    if (!selectedCommunityId) return;
    try {
      await updateRole.mutateAsync({
        communityId: selectedCommunityId,
        memberId,
        role,
      });
      invalidate();
      toast.success("Rol actualizado");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo actualizar el rol",
      );
    }
  };

  const handleAssignUnit = async (memberId: string) => {
    if (!selectedCommunityId) return;
    const hasUnit = memberUnitForm.unitId || memberUnitForm.newUnit?.unitNumber;
    if (!hasUnit) return;

    try {
      await assignUnit.mutateAsync({
        communityId: selectedCommunityId,
        memberId,
        unitId: memberUnitForm.unitId || undefined,
        newUnit: memberUnitForm.newUnit?.unitNumber
          ? {
              unitNumber: memberUnitForm.newUnit.unitNumber,
              blockSection: memberUnitForm.newUnit.blockSection || undefined,
            }
          : undefined,
        assignmentType: memberUnitForm.assignmentType,
      });
      invalidate();
      setMemberUnitForm({ unitId: "", newUnit: null, assignmentType: "OWNER" });
      toast.success("Unidad asignada");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo asignar la unidad",
      );
    }
  };

  const handleRemoveAssignment = async (assignmentId: string) => {
    if (!selectedCommunityId) return;
    try {
      await removeAssignment.mutateAsync({
        communityId: selectedCommunityId,
        assignmentId,
      });
      invalidate();
      toast.success("Unidad desasignada");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo desasignar la unidad",
      );
    }
  };

  if (!selectedCommunityId || !isAdmin) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold md:text-3xl">Miembros</h1>
          <p className="text-muted-foreground">
            Gestioná los miembros del barrio.
          </p>
        </div>
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">
              {!selectedCommunityId
                ? "Seleccioná un barrio para gestionar sus miembros."
                : "No tenés permisos de administrador en este barrio."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-6 md:px-6 md:py-10">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">Miembros</h1>
          <p className="text-muted-foreground">
            {currentCommunity?.name} &middot; {members?.length ?? 0}{" "}
            {members?.length === 1 ? "miembro" : "miembros"}
          </p>
        </div>
        <Dialog
          open={isAddOpen}
          onOpenChange={(open) => {
            setIsAddOpen(open);
            if (!open) {
              setNewMember({
                email: "",
                role: "RESIDENT",
                unitId: "",
                newUnit: null,
                assignmentType: "OWNER",
              });
            }
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              Agregar miembro
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar miembro</DialogTitle>
              <DialogDescription>
                Agregá un nuevo miembro al barrio por email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="member-email">Email</Label>
                <Input
                  id="member-email"
                  type="email"
                  placeholder="usuario@ejemplo.com"
                  value={newMember.email}
                  onChange={(e) =>
                    setNewMember({ ...newMember, email: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select
                  value={newMember.role}
                  onValueChange={(value: Role) =>
                    setNewMember({ ...newMember, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unidad (opcional)</Label>
                <Select
                  value={
                    newMember.newUnit
                      ? "__new__"
                      : newMember.unitId || "__none__"
                  }
                  onValueChange={(value) => {
                    if (value === "__none__") {
                      setNewMember({
                        ...newMember,
                        unitId: "",
                        newUnit: null,
                      });
                    } else if (value === "__new__") {
                      setNewMember({
                        ...newMember,
                        unitId: "",
                        newUnit: { unitNumber: "", blockSection: "" },
                      });
                    } else {
                      setNewMember({
                        ...newMember,
                        unitId: value,
                        newUnit: null,
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin unidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin unidad</SelectItem>
                    {units?.map((unit) => (
                      <SelectItem key={unit.id} value={unit.id}>
                        {unit.unitNumber}
                        {unit.blockSection && ` (${unit.blockSection})`}
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__">
                      + Crear nueva unidad
                    </SelectItem>
                  </SelectContent>
                </Select>
                {newMember.newUnit && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Número de unidad"
                      value={newMember.newUnit.unitNumber}
                      onChange={(e) =>
                        setNewMember({
                          ...newMember,
                          newUnit: {
                            ...newMember.newUnit!,
                            unitNumber: e.target.value,
                          },
                        })
                      }
                      className="flex-1"
                    />
                    <Input
                      placeholder="Manzana/Sector (opcional)"
                      value={newMember.newUnit.blockSection}
                      onChange={(e) =>
                        setNewMember({
                          ...newMember,
                          newUnit: {
                            ...newMember.newUnit!,
                            blockSection: e.target.value,
                          },
                        })
                      }
                      className="flex-1"
                    />
                  </div>
                )}
                {(newMember.unitId || newMember.newUnit) && (
                  <Select
                    value={newMember.assignmentType}
                    onValueChange={(value: AssignmentType) =>
                      setNewMember({ ...newMember, assignmentType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSIGNMENT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={handleAddMember}
                disabled={addMember.isPending || !newMember.email.trim()}
              >
                {addMember.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Agregar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Miembros del barrio</CardTitle>
          <CardDescription>
            Gestioná los miembros, roles y unidades asignadas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {members?.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              Todavía no hay miembros en este barrio.
            </p>
          ) : (
            <div className="space-y-2">
              {members?.map((member) => (
                <div key={member.id} className="rounded-xl border">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-xs font-medium">
                        {member.user.name?.slice(0, 2).toUpperCase() ||
                          member.user.email?.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {member.user.name || member.user.email}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">
                            {member.user.email}
                          </p>
                          {member.unitAssignments.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Home className="mr-1 h-3 w-3" />
                              {member.unitAssignments
                                .map((a) => a.unit.unitNumber)
                                .join(", ")}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={member.role}
                        onValueChange={(value: Role) =>
                          handleUpdateRole(member.id, value)
                        }
                      >
                        <SelectTrigger className="h-8 w-[170px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((role) => (
                            <SelectItem key={role.value} value={role.value}>
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Ver unidades"
                        onClick={() => {
                          if (expandedMemberId === member.id) {
                            setExpandedMemberId(null);
                            setMemberUnitForm({
                              unitId: "",
                              newUnit: null,
                              assignmentType: "OWNER",
                            });
                          } else {
                            setExpandedMemberId(member.id);
                            setMemberUnitForm({
                              unitId: "",
                              newUnit: null,
                              assignmentType: "OWNER",
                            });
                          }
                        }}
                      >
                        {expandedMemberId === member.id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label="Eliminar miembro"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              ¿Eliminar miembro?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Esto eliminará a{" "}
                              {member.user.name || member.user.email} del
                              barrio, incluyendo sus unidades asignadas.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRemoveMember(member.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {expandedMemberId === member.id && (
                    <div className="border-t px-4 py-3 space-y-3">
                      {member.unitAssignments.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground">
                            Unidades asignadas
                          </p>
                          {member.unitAssignments.map((assignment) => (
                            <div
                              key={assignment.id}
                              className="flex items-center justify-between rounded bg-muted/50 px-3 py-2"
                            >
                              <div className="flex items-center gap-2">
                                <Home className="h-3 w-3 text-muted-foreground" />
                                <span className="text-sm">
                                  {assignment.unit.unitNumber}
                                  {assignment.unit.blockSection &&
                                    ` (${assignment.unit.blockSection})`}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {ASSIGNMENT_TYPES.find(
                                    (t) => t.value === assignment.type,
                                  )?.label || assignment.type}
                                </Badge>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                aria-label="Desasignar unidad"
                                onClick={() =>
                                  handleRemoveAssignment(assignment.id)
                                }
                                disabled={
                                  removeAssignment.isPending &&
                                  removeAssignment.variables?.assignmentId ===
                                    assignment.id
                                }
                              >
                                {removeAssignment.isPending &&
                                removeAssignment.variables?.assignmentId ===
                                  assignment.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <X className="h-3 w-3" />
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">
                          Agregar unidad
                        </p>
                        <div className="flex gap-2">
                          <Select
                            value={
                              memberUnitForm.newUnit
                                ? "__new__"
                                : memberUnitForm.unitId || "__none__"
                            }
                            onValueChange={(value) => {
                              if (value === "__none__") {
                                setMemberUnitForm({
                                  ...memberUnitForm,
                                  unitId: "",
                                  newUnit: null,
                                });
                              } else if (value === "__new__") {
                                setMemberUnitForm({
                                  ...memberUnitForm,
                                  unitId: "",
                                  newUnit: {
                                    unitNumber: "",
                                    blockSection: "",
                                  },
                                });
                              } else {
                                setMemberUnitForm({
                                  ...memberUnitForm,
                                  unitId: value,
                                  newUnit: null,
                                });
                              }
                            }}
                          >
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Seleccionar unidad" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">
                                Seleccionar una unidad
                              </SelectItem>
                              {units?.map((unit) => (
                                <SelectItem key={unit.id} value={unit.id}>
                                  {unit.unitNumber}
                                  {unit.blockSection &&
                                    ` (${unit.blockSection})`}
                                </SelectItem>
                              ))}
                              <SelectItem value="__new__">
                                + Crear nueva unidad
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {(memberUnitForm.unitId ||
                            memberUnitForm.newUnit) && (
                            <Select
                              value={memberUnitForm.assignmentType}
                              onValueChange={(value: AssignmentType) =>
                                setMemberUnitForm({
                                  ...memberUnitForm,
                                  assignmentType: value,
                                })
                              }
                            >
                              <SelectTrigger className="w-[160px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ASSIGNMENT_TYPES.map((type) => (
                                  <SelectItem
                                    key={type.value}
                                    value={type.value}
                                  >
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        {memberUnitForm.newUnit && (
                          <div className="flex gap-2">
                            <Input
                              placeholder="Número de unidad"
                              value={memberUnitForm.newUnit.unitNumber}
                              onChange={(e) =>
                                setMemberUnitForm({
                                  ...memberUnitForm,
                                  newUnit: {
                                    ...memberUnitForm.newUnit!,
                                    unitNumber: e.target.value,
                                  },
                                })
                              }
                              className="flex-1"
                            />
                            <Input
                              placeholder="Manzana/Sector"
                              value={memberUnitForm.newUnit.blockSection}
                              onChange={(e) =>
                                setMemberUnitForm({
                                  ...memberUnitForm,
                                  newUnit: {
                                    ...memberUnitForm.newUnit!,
                                    blockSection: e.target.value,
                                  },
                                })
                              }
                              className="flex-1"
                            />
                          </div>
                        )}
                        {(memberUnitForm.unitId ||
                          memberUnitForm.newUnit?.unitNumber) && (
                          <Button
                            size="sm"
                            onClick={() => handleAssignUnit(member.id)}
                            disabled={assignUnit.isPending}
                            className="w-full"
                          >
                            {assignUnit.isPending ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Plus className="mr-2 h-4 w-4" />
                            )}
                            Asignar unidad
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
